import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
	PraticaListResponse,
	PraticaDetailResponse,
	SignMultiRemotaRequest,
	StartAuthOperationResponse,
	PageResponse,
	ListFirmatariResponse,
	FirmatarioStatus,
} from '../models/pratica.model';
import { ConfigService } from '../services/config.service';

@Injectable({ providedIn: 'root' })
export class FirmaService {
	private http = inject(HttpClient);
	private configService = inject(ConfigService);

	private authCache = new Map<string, string>(); // idTransaction -> operationID

	private get baseUrl() {
		return this.configService.configuration.apiUrl;
	}

	constructor() {}

	setAuthenticated(idTransaction: string, operationID: string) {
		this.authCache.set(idTransaction, operationID);
	}

	getAuthOperationID(idTransaction: string): string | null {
		return this.authCache.get(idTransaction) || null;
	}

	clearAuthCache() {
		this.authCache.clear();
	}

	listPraticheDaFirmare(page = 0, numElements = 100) {
		return this.http.get<PraticaListResponse>(this.baseUrl + '/api/firma/listPraticheDaFirmare', {
			params: {
				page: page.toString(),
				'num-elements': numElements.toString(),
			},
		});
	}

	listStoricoPraticheFirmatario(params: {
		page?: number;
		'num-elements'?: number;
		'start-date'?: string;
		'end-date'?: string;
		status?: number | string;
		'text-search'?: string;
		'sort-field'?: string;
		'sort-direction'?: string;
	}) {
		const httpParams: any = {
			page: (params.page ?? 0).toString(),
			'num-elements': (params['num-elements'] ?? 100).toString(),
			'start-date': params['start-date'] ?? '',
			'end-date': params['end-date'] ?? '',
			status: params.status !== undefined && params.status !== null ? params.status.toString() : '',
			'text-search': params['text-search'] ?? '',
			'sort-field': params['sort-field'] ?? '',
			'sort-direction': params['sort-direction'] ?? '',
		};

		return this.http.get<any>(this.baseUrl + '/api/firma/listStoricoPraticheFirmatario', {
			params: httpParams,
		});
	}

	listPraticheSegreteria(params: {
		page?: number;
		'num-elements'?: number;
		'start-date'?: string;
		'end-date'?: string;
		status?: number | string;
		'text-search'?: string;
		'sort-field'?: string;
		'sort-direction'?: string;
	}) {
		const httpParams: any = {
			page: (params.page ?? 0).toString(),
			'num-elements': (params['num-elements'] ?? 100).toString(),
			'start-date': params['start-date'] ?? '',
			'end-date': params['end-date'] ?? '',
			status: params.status !== undefined && params.status !== null ? params.status.toString() : '',
			'text-search': params['text-search'] ?? '',
			'sort-field': params['sort-field'] ?? '',
			'sort-direction': params['sort-direction'] ?? '',
		};

		return this.http.get<any>(this.baseUrl + '/api/firma/listPraticheSegreteria', {
			params: httpParams,
		});
	}

	getPraticaDetail(idTransaction: string) {
		return this.http.get<PraticaDetailResponse>(this.baseUrl + `/api/firmapublic/getTransactionStatus/${idTransaction}`);
	}

	signMultiRemota(ids: number[], otpCode: string | number) {
		const body = { ids, otpCode: typeof otpCode === 'string' ? parseInt(otpCode, 10) : otpCode };
		return this.http.post(this.baseUrl + '/api/firma/signMultiRemota', body);
	}

	startMultiOtp(ids: number[]) {
		return this.http.post(this.baseUrl + '/api/firma/startMultiOtp', { ids });
	}

	signMultiOtp(ids: number[], otpCode: string | number) {
		const body = { ids, otpCode: typeof otpCode === 'string' ? parseInt(otpCode, 10) : otpCode };
		return this.http.post(this.baseUrl + '/api/firma/signMultiOtp', body);
	}

	startAuthOperation(idTransaction: string, token: string) {
		const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
		return this.http.post<StartAuthOperationResponse>(
			this.baseUrl + `/api/firmapublic/startAuthOperation/${idTransaction}`,
			{},
			{ headers },
		);
	}

	openTransaction(idTransaction: string, token: string, otp: number, operationID: string) {
		const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
		const body = { operationID, otp };
		return this.http.post(this.baseUrl + `/api/firmapublic/openTransaction/${idTransaction}`, body, { headers });
	}

	getPage(idTransaction: string, documentID: number, pageNumber: number, token: string) {
		const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
		return this.http.get<PageResponse>(this.baseUrl + `/api/firmapublic/getPage/${idTransaction}`, {
			headers,
			params: {
				documentID: documentID.toString(),
				pageNumber: pageNumber.toString(),
			},
		});
	}

	signDocument(idTransaction: string, body: any, token: string) {
		const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
		return this.http.post(this.baseUrl + `/api/firmapublic/signDocument/${idTransaction}`, body, { headers });
	}

	closeTransaction(idTransaction: string, token: string) {
		const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
		return this.http.post(this.baseUrl + `/api/firmapublic/closeTransaction/${idTransaction}`, {}, { headers });
	}

	downloadPratica(id: number) {
		return this.http.get(this.baseUrl + `/api/firma/download/${id}`, {
			responseType: 'blob',
		});
	}

	refuseTransaction(idTransaction: string, message: string, token: string) {
		const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
		const body = { message };
		return this.http.post(this.baseUrl + `/api/firmapublic/refuseTransaction/${idTransaction}`, body, { headers });
	}

	startPratica(praticaJson: any, files: File[]) {
		const formData = new FormData();
		// Avvolgimento del JSON in un Blob per specificare il content-type application/json
		const blob = new Blob([JSON.stringify(praticaJson)], { type: 'application/json' });
		formData.append('pratica', blob);

		files.forEach((file) => formData.append('files', file));

		return this.http.post<any>(this.baseUrl + '/api/firma/startPratica', formData);
	}

	startAddFirme(id: number) {
		return this.http.get<any>(this.baseUrl + `/api/firma/startAddFirme/${id}`);
	}

	endAddFirme(body: { id: number; puntiFirma: any[] }) {
		return this.http.post(this.baseUrl + '/api/firma/endAddFirme', body, { responseType: 'text' });
	}

	endAddFirmeStatus(id: number) {
		return this.http.get<{ status: string }>(this.baseUrl + `/api/firma/endAddFirmeStatus/${id}`);
	}

	firmatariStatus(id: number) {
		return this.http.get<{ firmatari: FirmatarioStatus[] }>(this.baseUrl + `/api/firma/firmatariStatus/${id}`);
	}

	deletePraticaSegreteria(id: number) {
		return this.http.delete(this.baseUrl + `/api/firma/delete/${id}`);
	}

	sollecitaPratica(id: number) {
		return this.http.get(this.baseUrl + `/api/firma/sollecita/${id}`);
	}

	listFirmatari(params?: { page?: number; 'num-elements'?: number; 'text-search'?: string }) {
		const httpParams: any = {
			page: (params?.page ?? 0).toString(),
			'num-elements': (params?.['num-elements'] ?? 10).toString(),
			'text-search': params?.['text-search'] ?? '',
		};
		return this.http.get<ListFirmatariResponse>(this.baseUrl + '/api/firma/listFirmatari', {
			params: httpParams,
		});
	}

	insertRubrica(payload: any) {
		return this.http.post(this.baseUrl + '/api/rubrica/insertRubrica', payload);
	}
}
